/**
 * PdfModal Component
 * Shows the current PDF in a modal (PDF.js) with a viewer-style toolbar:
 * page navigation on the left, download / print / close on the right
 */

import { i18n } from '../utils/i18n.js';
import { PDFViewer } from './PDFViewer.js';

export class PdfModal {
  constructor({ container, pdfData, fileName = 'documento.pdf', onDownload, onError }) {
    this.container = container;
    this.pdfData = pdfData;
    this.fileName = fileName;
    this.onDownload = onDownload;
    this.onError = onError;
    this.element = null;
    this.pdfViewer = null;
    this.previouslyFocused = null;
    this.currentPage = 1;
    this.handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    };
    this.handleScroll = () => this.updateCurrentPage();
  }

  render() {
    this.previouslyFocused = document.activeElement;

    const iconBtn = 'p-2 rounded-md text-gray-200 hover:bg-gray-700 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-40 disabled:cursor-not-allowed';

    // Appended (not innerHTML on container) so the panel content is preserved
    this.element = document.createElement('div');
    this.element.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-0 sm:p-4';
    this.element.innerHTML = `
      <div class="bg-gray-100 sm:rounded-lg shadow-xl w-full h-full max-w-5xl flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-label="${i18n.t('pdfModal.title')}">
        <div class="flex items-center justify-between gap-2 px-2 py-1 bg-gray-800">
          <!-- Page navigation (hidden until the PDF is rendered with PDF.js) -->
          <div class="flex items-center gap-1 invisible" data-wsp-pdf-nav>
            <button type="button" id="wsp-pdf-modal-prev" class="${iconBtn}" title="${i18n.t('pdfModal.prevPage')}" aria-label="${i18n.t('pdfModal.prevPage')}">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
              </svg>
            </button>
            <button type="button" id="wsp-pdf-modal-next" class="${iconBtn}" title="${i18n.t('pdfModal.nextPage')}" aria-label="${i18n.t('pdfModal.nextPage')}">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
            <span id="wsp-pdf-modal-page" class="px-2 text-sm text-gray-200 whitespace-nowrap" aria-live="polite"></span>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-1">
            <button type="button" id="wsp-pdf-modal-download" class="${iconBtn}" title="${i18n.t('pdfModal.download')}" aria-label="${i18n.t('pdfModal.download')}">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <button type="button" id="wsp-pdf-modal-print" class="${iconBtn}" title="${i18n.t('button.print')}" aria-label="${i18n.t('button.print')}">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fill-rule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clip-rule="evenodd"/>
              </svg>
            </button>
            <span class="w-px h-6 mx-1 bg-gray-600" aria-hidden="true"></span>
            <button type="button" id="wsp-pdf-modal-close" class="${iconBtn}" title="${i18n.t('button.close')}" aria-label="${i18n.t('button.close')}">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="flex-1 min-h-0 wsp-pdf-modal-viewer"></div>
      </div>
    `;

    this.container.appendChild(this.element);
    this.attachEventListeners();
    this.element.querySelector('#wsp-pdf-modal-close').focus();

    // Reuse PDFViewer, always with PDF.js (own toolbar instead of the native one)
    this.pdfViewer = new PDFViewer({
      container: this.element.querySelector('.wsp-pdf-modal-viewer'),
      pdfData: this.pdfData,
      forcePdfJs: true
    });
    this.pdfViewer.render().then(() => this.setupPageNavigation());
  }

  attachEventListeners() {
    this.element.querySelector('#wsp-pdf-modal-prev').addEventListener('click', () => this.goToPage(this.currentPage - 1));
    this.element.querySelector('#wsp-pdf-modal-next').addEventListener('click', () => this.goToPage(this.currentPage + 1));
    this.element.querySelector('#wsp-pdf-modal-download').addEventListener('click', () => this.handleDownload());
    this.element.querySelector('#wsp-pdf-modal-print').addEventListener('click', () => this.handlePrint());
    this.element.querySelector('#wsp-pdf-modal-close').addEventListener('click', () => this.close());

    // Backdrop click closes
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) this.close();
    });

    document.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Enable page navigation once the pages are rendered.
   * If PDF.js failed and the viewer fell back to an iframe, navigation stays hidden.
   */
  setupPageNavigation() {
    if (!this.element) return; // Closed before rendering finished

    this.pagesContainer = this.element.querySelector('[data-wsp-pdf-pages]');
    if (!this.pagesContainer || !this.pdfViewer.totalPages) return;

    this.pagesContainer.addEventListener('scroll', this.handleScroll, { passive: true });
    this.element.querySelector('[data-wsp-pdf-nav]').classList.remove('invisible');
    this.updatePageControls();
  }

  getPageCanvas(pageNum) {
    return this.pagesContainer?.querySelector(`[data-wsp-pdf-page="${pageNum}"]`);
  }

  /**
   * Distance from the top of the visible area to the top of a page (px)
   */
  getPageOffset(canvas) {
    return canvas.getBoundingClientRect().top - this.pagesContainer.getBoundingClientRect().top;
  }

  goToPage(pageNum) {
    const canvas = this.getPageCanvas(pageNum);
    if (!canvas) return;
    this.pagesContainer.scrollTop += this.getPageOffset(canvas) - 8;
    this.currentPage = pageNum;
    this.updatePageControls();
  }

  /**
   * Current page = last page whose top is above the middle of the visible area
   */
  updateCurrentPage() {
    const middle = this.pagesContainer.clientHeight / 2;
    let page = 1;
    for (let i = 1; i <= this.pdfViewer.totalPages; i++) {
      const canvas = this.getPageCanvas(i);
      if (canvas && this.getPageOffset(canvas) <= middle) page = i;
    }
    if (page !== this.currentPage) {
      this.currentPage = page;
      this.updatePageControls();
    }
  }

  updatePageControls() {
    const total = this.pdfViewer.totalPages;
    this.element.querySelector('#wsp-pdf-modal-page').textContent =
      i18n.t('pdfModal.pageOf', { page: this.currentPage, total });
    this.element.querySelector('#wsp-pdf-modal-prev').disabled = this.currentPage <= 1;
    this.element.querySelector('#wsp-pdf-modal-next').disabled = this.currentPage >= total;
  }

  async handleDownload() {
    try {
      const blob = await this.pdfViewer.download(this.fileName);
      if (this.onDownload) this.onDownload(blob);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      if (this.onError) this.onError(i18n.t('error.download') + ': ' + error.message);
    }
  }

  async handlePrint() {
    try {
      await this.pdfViewer.print();
    } catch (error) {
      console.error('Failed to print PDF:', error);
      if (this.onError) this.onError(i18n.t('error.print') + ': ' + error.message);
    }
  }

  close() {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.pagesContainer?.removeEventListener('scroll', this.handleScroll);
    this.pdfViewer?.destroy();
    this.element?.remove();
    this.element = null;
    this.previouslyFocused?.focus?.();
  }
}
